import { api, ApiError } from "../../services/apiClient";
import { normalizePhone } from "../../services/phoneNumber";

// Registered users of the public phone flow (Admin → Settings). Pure helpers + typed API calls
// over /api/v1/admin/registered-users (behind the admin session gate). The server re-validates
// and normalizes everything; these helpers only give immediate feedback.

export interface RegisteredUserRow {
  id: number;
  /** Canonical E.164 lookup value. */
  phone: string;
  /** Human display form (e.g. 050-123-4567). */
  phoneDisplay: string;
  displayName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RegisteredUserForm {
  phone: string;
  displayName: string;
}

export type RegUserErrorKey = "invalidPhone" | "nameRequired" | "duplicate" | "notFound" | "generic";

export const MAX_DISPLAY_NAME = 128;

export function validateRegisteredUserForm(
  form: RegisteredUserForm
): { ok: true; phone: string; displayName: string } | { ok: false; error: RegUserErrorKey } {
  const phone = normalizePhone(form.phone);
  if (!phone) return { ok: false, error: "invalidPhone" };
  const displayName = form.displayName.trim();
  if (!displayName || displayName.length > MAX_DISPLAY_NAME) return { ok: false, error: "nameRequired" };
  return { ok: true, phone, displayName };
}

export function regUserErrorKey(err: unknown): RegUserErrorKey {
  if (err instanceof ApiError) {
    switch (err.problem.type) {
      case "VC-PUB-002": return "invalidPhone";
      case "VC-REG-001": return "nameRequired";
      case "VC-REG-002": return "duplicate";
      case "VC-REG-404": return "notFound";
    }
  }
  return "generic";
}

/** Active users first, then by display name (locale-aware, so Hebrew sorts correctly). */
export function sortRegisteredUsers(rows: RegisteredUserRow[]): RegisteredUserRow[] {
  return [...rows].sort((a, b) =>
    a.isActive !== b.isActive ? (a.isActive ? -1 : 1) : a.displayName.localeCompare(b.displayName) || a.id - b.id);
}

export function upsertRow(rows: RegisteredUserRow[], row: RegisteredUserRow): RegisteredUserRow[] {
  return sortRegisteredUsers([...rows.filter((r) => r.id !== row.id), row]);
}

export function removeRow(rows: RegisteredUserRow[], id: number): RegisteredUserRow[] {
  return rows.filter((r) => r.id !== id);
}

const BASE = "/admin/registered-users";

export const listRegisteredUsers = (token: string) => api.get<RegisteredUserRow[]>(BASE, token);

export const createRegisteredUser = (token: string, body: { phone: string; displayName: string; isActive: boolean }) =>
  api.post<RegisteredUserRow>(BASE, body, token);

export const updateRegisteredUser = (token: string, id: number, body: { phone?: string; displayName?: string; isActive?: boolean }) =>
  api.put<RegisteredUserRow>(`${BASE}/${id}`, body, token);

export const removeRegisteredUser = (token: string, id: number) =>
  api.delete<{ outcome: "removed" | "deactivated" }>(`${BASE}/${id}`, token);
